import _ from 'lodash';
import { InputFieldConfig } from 'src/@vymo/ui/molecules/form/types';
import { createSelector } from '@reduxjs/toolkit';
import { getRevyreConfig } from 'src/models/lmsConfig/selectors';
import { RootState } from 'src/store';

export const getFilterData = (state: RootState) =>
  state?.homeDashboard?.filterData;

export const getIsLoading = (state: RootState) => state?.homeDashboard?.loading;

export const getHomeDashboardCards = (state: RootState) =>
  state?.homeDashboard?.helloCardData;

export const getHomeDashboardLeadMetrics = createSelector(
  [getHomeDashboardCards],
  (helloCards) => _.filter(helloCards, { type: 'leads' }) || [],
);

export const getHomeDashboardWidgetCards = createSelector(
  [getHomeDashboardCards],
  (helloCards) => _.filter(helloCards, { type: 'widget_card' }) || [],
);

export const getHelloCardPageData = (state: RootState) =>
  state?.homeDashboard?.helloCardPageData;

export const getCalendarData = (state: RootState) =>
  state?.homeDashboard?.calenderData;

export const getCalendarActivitiesList = (state: RootState) =>
  state?.homeDashboard?.activitiesList;

export const getLeadDetails = (state: RootState) =>
  state?.homeDashboard?.leadDetails;

export const getCalendarActivityDetails = (state: RootState) =>
  state?.homeDashboard?.activityDetails;

export const getRevyreHelloCards = (state: RootState) =>
  state.homeDashboard?.workflowHelloCards;

export const getWorkflowHelloCards = createSelector(
  [getRevyreHelloCards],
  (helloCards) => helloCards.filter((card) => card.type === 'workflow'),
);

export const selectModifiedActivityTypes = createSelector(
  [getCalendarActivitiesList],
  (activityTypes = []) => {
    if (!Array.isArray(activityTypes)) return [];

    const modified = activityTypes.map((activity) => ({
      ...activity,
      name: activity.module
        ? `${activity.name} · ${activity.module}`
        : activity.name,
    }));

    modified.unshift({
      code: ' ',
      name: 'All Activities',
    });

    return modified;
  },
);

export const getInputsToShowOnCalendarDetails = createSelector(
  [getCalendarActivityDetails, getLeadDetails],
  (calendarDetails, leadDetails) => {
    if (calendarDetails && Object.keys(calendarDetails).length > 0) {
      const { inputs } = calendarDetails.info;
      return inputs;
    }

    if (leadDetails && Object.keys(leadDetails).length > 0) {
      return leadDetails.inputs;
    }

    return null;
  },
);

export const getSnapShotCards = (state: RootState) =>
  state?.homeDashboard?.snapShotCards;

export const getSnapShotMetrics = (state: RootState) =>
  state?.homeDashboard?.snapShotMetrics;

// Combined selector that merges cards and metrics data
export const getPerformanceSnapshot = createSelector(
  [getSnapShotCards, getSnapShotMetrics],
  (cards, metrics) => {
    if (!cards || !Array.isArray(cards) || cards.length === 0) {
      return [];
    }

    // Flatten all items from all cards
    const allItems = cards
      .filter((card) => card.type === 'metric')
      .flatMap((card) => card.items || []);

    // Combine each item with its corresponding metric data
    const combinedData = allItems
      .map((item) => {
        const metricData = metrics?.find((m) => m.code === item.code);

        // Only include items that have successful metric data
        if (metricData?.data?.status === 'SUCCESS') {
          return {
            code: item.code,
            name: item.name || item.meta_data?.goal_name || item.code,
            meta_data: item.meta_data,
            data: metricData.data,
          };
        }
        return null;
      })
      .filter((item) => item !== null);

    return combinedData;
  },
);

export const getRevyreActivityInputsAndGroups = createSelector(
  [
    (state) => getRevyreConfig(state),
    (state, domainType, activityDetails) => activityDetails || {},
    (state, domainType, activityDetails) =>
      activityDetails?.mutatedEntity || {},
    (state, domainType) => domainType,
  ],
  (revyreConfig, entityDetails, entityDetailsInputMap, domainType) => {
    const domainConfig = revyreConfig?.[domainType];
    const { createConfig } = domainConfig ?? {};
    let { detailsConfig } = domainConfig ?? {};

    const { activityType, workflowType: parentAssociatedWorkflow } =
      entityDetailsInputMap;

    if (parentAssociatedWorkflow && activityType) {
      detailsConfig = {
        inputs: revyreConfig?.[parentAssociatedWorkflow]?.activityConfig?.find(
          (config) => config.type === activityType,
        )?.detail_inputs,
        groups: [],
      };
    } else {
      return { inputs: [], groups: [] };
    }

    const { inputs, groups = [] } = detailsConfig ??
      createConfig ?? { inputs: [], groups: [] };

    const { mutatedEntity } = entityDetails ?? {};

    const filteredInputs = _.filter(
      inputs,
      (input) => mutatedEntity?.[input?.code],
    ) as InputFieldConfig[];

    return { inputs: filteredInputs, groups };
  },
);
